import { PanelLeft } from 'lucide-react'
import type { MDXMetadata } from 'mdx/types'
import { Fragment } from 'react'
import { Link, useLocation } from '#router'
import { cn } from '@/lib/utils'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb'
import { Button } from './ui/button'

function* getAncestors(page: MDXMetadata): Generator<MDXMetadata> {
  if (page.parent && page.type !== 'course') yield* getAncestors(page.parent)
  yield page
}

export default function StickyNav({
  sidebarOpen,
  onSidebarToggle,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  sidebarOpen: boolean
  onSidebarToggle: React.MouseEventHandler<HTMLButtonElement>
}) {
  const { page } = useLocation()
  if (!page) return null

  const flattened = Array.from(getAncestors(page))

  return (
    <div
      className={cn(
        'bg-background/85 supports-[backdrop-filter]:bg-background/75 sticky top-0 z-10 w-full border-b backdrop-blur transition-all',
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          'mx-auto flex items-center gap-2 px-4 py-2',
          !sidebarOpen && 'container',
        )}
      >
        <Button
          variant="ghost"
          size="icon-sm"
          title="Close/Open Sidebar"
          onClick={onSidebarToggle}
        >
          <PanelLeft />
        </Button>
        <div className="bg-border h-4 w-px" />
        <Breadcrumb>
          <BreadcrumbList className="flex flex-nowrap overflow-auto">
            {flattened.map((item, index) => (
              <Fragment key={item.href}>
                {index < flattened.length - 1 ? (
                  <>
                    <BreadcrumbItem className="flex-none">
                      <BreadcrumbLink asChild>
                        <Link href={item.href}>{item.title}</Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                  </>
                ) : (
                  <BreadcrumbItem className="flex-none">
                    <BreadcrumbPage>{item.title}</BreadcrumbPage>
                  </BreadcrumbItem>
                )}
              </Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </div>
  )
}
